import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CTASection = () => {
  const navigate = useNavigate();

  const handleGetInTouch = () => {
    window.open('tel:+918919405132', '_self');
  };

  const handleShopNow = () => {
    navigate('/product-collection');
  };

  const benefits = [
  {
    icon: "Truck",
    title: "Free Shipping",
    description: "On all orders above ₹999"
  },
  {
    icon: "RotateCcw",
    title: "30-Day Returns",
    description: "Hassle-free returns & exchanges"
  },
  {
    icon: "Shield",
    title: "Lifetime Warranty",
    description: "Premium protection guarantee"
  },
  {
    icon: "Headphones",
    title: "24/7 Support",
    description: "Expert customer assistance"
  }];

  return (
    <section className="py-20 bg-gradient-to-br from-primary to-accent overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center text-white">
          {/* Main CTA Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12">

            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Icon name="Star" size={16} />
              <span className="text-sm font-medium">Join 50,000+ Happy Customers</span>
            </div>

            <h2 className="text-3xl md:text-5xl font-poppins font-bold mb-6 leading-tight">
              Ready to Transform Your
              <span className="block bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                Hydration Experience?
              </span>
            </h2>

            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
              Join the Mava family and discover the perfect blend of innovation, sustainability, and style. 
              Every bottle tells a story of conscious living.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}>

                <Button
                size="lg"
                onClick={handleShopNow}
                className="bg-white text-primary hover:bg-gray-50 font-semibold px-8 py-4 shadow-lg">

                  <Icon name="ShoppingCart" size={20} className="mr-2" />
                  Shop Collection
                </Button>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}>

                <Button
                variant="outline"
                size="lg"
                onClick={handleGetInTouch}
                className="border-2 border-white text-white hover:bg-white hover:text-primary font-semibold px-8 py-4">

                  <Icon name="Phone" size={20} className="mr-2" />
                  Get In Touch
                </Button>
              </motion.div>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              {benefits?.map((benefit, index) => (
                <motion.div
                  key={benefit?.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="text-center p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20"
                >
                  <div className="w-12 h-12 mx-auto mb-3 bg-white/20 rounded-full flex items-center justify-center">
                    <Icon name={benefit?.icon} size={20} className="text-white" />
                  </div>
                  <h3 className="text-sm font-semibold mb-1">{benefit?.title}</h3>
                  <p className="text-xs text-white/70">{benefit?.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center">
              <Icon name="Heart" size={24} className="text-white" />
            </div>
            <h3 className="text-xl font-poppins font-bold text-white mb-3">Trusted by 10,000+ Customers</h3>
            <p className="text-white/70">Join thousands of satisfied customers who trust Mava for their daily hydration needs.</p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center">
              <Icon name="Star" size={24} className="text-white" />
            </div>
            <h3 className="text-xl font-poppins font-bold text-white mb-3">50,000+ Happy Customers</h3>
            <p className="text-white/70">Join the Mava family and discover the perfect blend of innovation, sustainability, and style.</p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center">
              <Icon name="Shield" size={24} className="text-white" />
            </div>
            <h3 className="text-xl font-poppins font-bold text-white mb-3">Premium Quality Guarantee</h3>
            <p className="text-white/70">Every bottle tells a story of conscious living and premium quality.</p>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white/20 rounded-full animate-pulse" />
          <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-white/30 rounded-full animate-ping" />
          <div className="absolute bottom-1/4 left-1/2 w-3 h-3 bg-white/10 rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default CTASection;